// A small JPEG of a photo about to be uploaded (PRD 21 §6.2), sent beside it so
// the inbox and the register can show a postage stamp without fetching the
// whole photo. The api has no image library to make one, and a phone's receipt
// photo is megabytes; this page has the photo decoded already.
//
// Null whenever the browser cannot draw it: a PDF, a HEIC outside Safari, a
// file that does not decode. The upload goes ahead without one, and the page
// shows the file's icon instead.

/** The long edge, in pixels: two and a half times the largest place it is shown. */
export const THUMBNAIL_EDGE = 320;

async function decode(file: File): Promise<CanvasImageSource & { width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    // from-image: a phone photo is stored sideways with an EXIF turn, and a
    // thumbnail that ignored it would show every receipt lying down.
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function drawThumbnail(file: File, edge: number = THUMBNAIL_EDGE): Promise<Blob | null> {
  if (!file.type.startsWith('image/')) return null;
  try {
    const source = await decode(file);
    const scale = Math.min(1, edge / Math.max(source.width, source.height));
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    // A JPEG has no transparency: a see-through PNG would otherwise come out
    // on black. This is the picture's own background, not the page's colour.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);
    if ('close' in source && typeof source.close === 'function') source.close();
    return await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
  } catch {
    return null;
  }
}
